import React, { useState, useRef, useEffect } from "react";
import { useQuote } from "../context/QuoteContext";
import api from "../api";
import { uid } from "../utils";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Send, Loader2, Bot, User, Sparkles, ListTree, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ObjectsWorkArea from "./ObjectsWorkArea";
import AreaWorkArea from "./AreaWorkArea";

// render WhatsApp markdown *bold* / _italic_
const renderFormatted = (line, i) => {
  const nodes = [];
  let buf = "", bold = false, italic = false, key = 0;
  const flush = () => {
    if (!buf) return;
    let node;
    if (bold && italic) node = <strong key={key}><em>{buf}</em></strong>;
    else if (bold) node = <strong key={key}>{buf}</strong>;
    else if (italic) node = <em key={key}>{buf}</em>;
    else node = <span key={key}>{buf}</span>;
    nodes.push(node); key += 1; buf = "";
  };
  for (const ch of line) {
    if (ch === "*") { flush(); bold = !bold; }
    else if (ch === "_") { flush(); italic = !italic; }
    else buf += ch;
  }
  flush();
  return <div key={i} className="min-h-[6px]">{nodes}</div>;
};

const SUGGESTIONS = [
  "10 scatole 60x40x40 + costo ritiro 20090 Buccinasco, per 12 mesi",
  "Cliente cerca spazio di circa 10 m², soprattutto scatole, ritiro 20124 Milano",
  "30 scatole + 1 lavatrice + 1 comodino, ritiro Monza, per 8 mesi",
];

export default function SimpleChatWorkArea() {
  const {
    chatMessages, setChatMessages, setChatMessage, clienteNome, setClienteNome,
    catalog, resolveEntries, replaceItems, setRitiro, setAbitiBox, items,
    areaData, setAreaData,
  } = useQuote();
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chatMessages, loading]);

  const findCat = (label) => catalog.find((c) => c.label.toLowerCase() === String(label || "").toLowerCase());
  const complete = (r) => r.l && r.w && r.h;

  const parseArea = (text) => {
    const m = String(text || "").match(/(\d+(?:[.,]\d+)?)\s*(?:m²|m2|mq|metri\s*quadr\w*|metr[oi]\s*quadr\w*)/i);
    return m ? parseFloat(m[1].replace(",", ".")) : 0;
  };

  const deepAnalyze = async () => {
    const chatText = (chatMessages.filter((m) => m.role === "user").map((m) => m.content).join("\n\n")
      || chatMessages.map((m) => m.content).join("\n\n")).trim();
    if (!chatText) { toast.error("Scrivi prima la richiesta nella chat"); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/ai/extract-items", { text: chatText });
      const areaVal = parseArea(chatText);
      const nm = (data.clientName || "").trim();

      // Build the extracted objects (may be empty)
      let list = (data.items || []).map((it) => {
        const c = findCat(it.catalogMatch) || findCat(it.label);
        return {
          label: it.label, qty: it.qty || 1, catalogMatch: it.catalogMatch,
          l: it.length_cm || (c ? c.length_cm : "") || "",
          w: it.width_cm || (c ? c.width_cm : "") || "",
          h: it.height_cm || (c ? c.height_cm : "") || "",
          dimsFromText: !!it.dims_from_text && !!(it.length_cm || it.width_cm || it.height_cm),
        };
      });
      const missing = list.filter((r) => !complete(r));
      if (missing.length) {
        try {
          const { data: est } = await api.post("/ai/estimate-prices", {
            items: missing.map((m) => ({ label: m.label, dims: { l: +m.l || 0, w: +m.w || 0, h: +m.h || 0 } })),
          });
          const byLabel = {};
          (est.estimates || []).forEach((e) => { byLabel[String(e.label).toLowerCase()] = e; });
          list = list.map((r) => {
            if (complete(r)) return r;
            const e = byLabel[String(r.label).toLowerCase()];
            if (!e) return r;
            return { ...r, l: r.l || e.length_cm || "", w: r.w || e.width_cm || "", h: r.h || e.height_cm || "" };
          });
        } catch { /* ignore */ }
      }
      const built = (await resolveEntries(list.map((r) => ({
        label: r.label, qty: r.qty, catalogMatch: r.catalogMatch,
        dims: { l: parseFloat(r.l) || 0, w: parseFloat(r.w) || 0, h: parseFloat(r.h) || 0 },
        dimsFromText: !!r.dimsFromText,
      }))))
        // drop vague, unpriceable items (e.g. "mobili vari" from an "X m²" phrase already counted as area)
        .filter((it) => (it.pricePerMonth || 0) > 0 || it.fromCatalog
          || (it.dims && ((it.dims.l || 0) > 0 || (it.dims.w || 0) > 0 || (it.dims.h || 0) > 0)));
      const hasObjects = built.length > 0;

      // Common fields
      if (data.cap) setRitiro((r) => ({ ...r, cap: data.cap }));
      if (nm && !(clienteNome || "").trim()) setClienteNome(nm);
      if (/\b(abiti|abito|vestit|capi|capo|camic|magliett|cappott|piumin|giacc|guardaroba|maglion|felp|pantalon|gonn)/i.test(chatText)) setAbitiBox(true);

      if (areaVal > 0 && hasObjects) {
        // COMBINATION: objects + area (area added as a "Deposito per volume" line item)
        setAreaData((a) => ({ ...a, area: areaVal }));
        const a = { ...areaData, area: areaVal };
        const volNetto = (a.area || 0) * (a.altezza || 0) * ((a.riempimento || 0) / 100);
        const price = volNetto * (a.tariffaM3 || 0);
        const areaItem = {
          id: uid(), tipo: "Deposito per volume", qty: 1, dims: { l: 0, w: 0, h: 0 },
          pricePerMonth: price, note: `${a.area} m² × ${a.altezza} m × ${a.riempimento}%`,
          fachMinutes: 0, imbMinutes: 0, isPriceOverridden: true, isDimOverridden: false,
          fromCatalog: false, _volumeM3: volNetto,
        };
        replaceItems([areaItem, ...built]);
        toast.success(`${built.length} oggetti + area ${areaVal} m² compilati dalla chat`);
      } else if (areaVal > 0) {
        // AREA only
        replaceItems([]);
        setAreaData((a) => ({ ...a, area: areaVal }));
        toast.success(`Stima per area: ${areaVal} m² compilata dalla chat`);
      } else {
        // OBJECTS only
        setAreaData((a) => ({ ...a, area: 0 }));
        replaceItems(built);
        toast.success(`${built.length} oggetti estratti dalla chat`);
      }
    } catch {
      toast.error("Errore durante l'analisi approfondita");
    } finally {
      setAnalyzing(false);
    }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const nextHistory = [...chatMessages, { role: "user", content }];
    setChatMessages(nextHistory);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/simple-chat", { messages: nextHistory });
      const reply = data.reply || "";
      setChatMessages([...nextHistory, { role: "assistant", content: reply }]);
      setChatMessage(reply);
      if (data.clientName && !(clienteNome || "").trim()) setClienteNome(data.clientName);
    } catch {
      toast.error("Errore nella generazione della risposta");
      setChatMessages(nextHistory); // keep user msg, allow retry
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="simple-chat-workarea">
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm flex flex-col h-[560px]">
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-100">
          <div className="h-9 w-9 rounded-lg bg-[#1a5f5a]/10 flex items-center justify-center">
            <Bot className="h-5 w-5 text-[#1a5f5a]" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Assistente StorMi</div>
            <div className="text-xs text-slate-500">Descrivi la richiesta: genero il messaggio WhatsApp</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {chatMessages.length === 0 && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <div className="h-12 w-12 rounded-full bg-[#1a5f5a]/10 flex items-center justify-center mb-3">
                <Sparkles className="h-6 w-6 text-[#1a5f5a]" />
              </div>
              <p className="text-sm text-slate-600 font-medium mb-1">Incolla la nota di chiamata o l'elenco oggetti</p>
              <p className="text-xs text-slate-400 mb-4">L'assistente segue le istruzioni StorMi e produce il preventivo WhatsApp pronto.</p>
              <div className="w-full space-y-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} data-testid={`chat-suggestion-${i}`}
                    className="w-full text-left text-xs text-slate-600 bg-slate-50 hover:bg-[#1a5f5a]/5 border border-slate-200 rounded-md px-3 py-2 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {chatMessages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`chat-msg-${m.role}-${i}`}>
              {m.role === "assistant" && (
                <div className="h-7 w-7 rounded-full bg-[#1a5f5a]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="h-4 w-4 text-[#1a5f5a]" />
                </div>
              )}
              <div className={`max-w-[80%] px-3 py-2 rounded-lg text-[13.5px] leading-snug whitespace-pre-wrap break-words ${
                m.role === "user"
                  ? "bg-[#1a5f5a] text-white rounded-br-sm selection:bg-white selection:text-[#1a5f5a]"
                  : "bg-slate-100 text-slate-800 rounded-bl-sm selection:bg-[#1a5f5a] selection:text-white"}`}>
                {m.role === "assistant" ? m.content.split("\n").map(renderFormatted) : m.content}
              </div>
              {m.role === "user" && (
                <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                  <User className="h-4 w-4 text-slate-500" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-2 justify-start" data-testid="chat-loading">
              <div className="h-7 w-7 rounded-full bg-[#1a5f5a]/10 flex items-center justify-center shrink-0">
                <Bot className="h-4 w-4 text-[#1a5f5a]" />
              </div>
              <div className="bg-slate-100 rounded-lg px-3 py-2 flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Sto scrivendo…
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 p-3">
          <div className="flex items-end gap-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
              rows={2} placeholder="Scrivi qui la richiesta del cliente… (Invio per inviare, Shift+Invio a capo)"
              className="flex-1 resize-none text-sm" data-testid="chat-input" disabled={loading} />
            <Button onClick={() => send()} disabled={loading || !input.trim()} data-testid="chat-send-button"
              className="h-10 w-10 p-0 bg-[#1a5f5a] hover:bg-[#134744] shrink-0">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {chatMessages.some((m) => m.role === "assistant") && (
        <Button onClick={deepAnalyze} disabled={analyzing} variant="outline" data-testid="deep-analysis-button"
          className="w-full gap-2 h-10 border-[#1a5f5a]/40 text-[#1a5f5a] hover:bg-[#1a5f5a]/5">
          {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : ((items.length > 0 || (areaData.area || 0) > 0) ? <RefreshCw className="h-4 w-4" /> : <ListTree className="h-4 w-4" />)}
          {(items.length > 0 || (areaData.area || 0) > 0) ? "Rianalizza i campi dalla chat" : "Analisi approfondita (compila tutti i campi)"}
        </Button>
      )}

      {(items.length > 0 || (areaData.area || 0) > 0) && (
        <div className="space-y-5 pt-1" data-testid="deep-analysis-sections">
          <div className="text-xs text-slate-400 flex items-center gap-1.5">
            <ListTree className="h-3.5 w-3.5" />
            {items.length > 0 ? "Campi (oggetti" : "Campi (per area"}{items.length > 0 && (areaData.area || 0) > 0 ? " + area" : ""}{items.length > 0 ? ")" : ")"} compilati dalla chat — modificabili. Continua a chattare e premi "Rianalizza i campi dalla chat" per aggiornarli.
          </div>
          {items.length > 0 ? <ObjectsWorkArea /> : <AreaWorkArea embedded />}
        </div>
      )}
    </div>
  );
}
